import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import {
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  BookOpen,
  CalendarDays,
  ChevronRight,
  createIcons,
  Headphones,
  Languages,
  LifeBuoy,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Mail,
  Menu,
  RefreshCw,
  ShieldCheck,
  UserRound,
  X
} from "lucide";

import {
  createAccountCheckout,
  createAccountPortalSession,
  fetchAccountStatus
} from "./accountApi";
import { resolveAccountAuthConfig } from "./accountConfig";
import {
  accountPlanReferenceAmount,
  accountPlanSavingsPercent,
  accountPreviewMode,
  escapeAccountHtml,
  formatAccountCurrency,
  formatAccountPlanPrice,
  isAccountPlanId,
  nativeMembershipManagement,
  previewStatus,
  type AccountPlanId,
  type AccountPreviewMode,
  type AccountStatus
} from "./accountModels";

const root = document.querySelector<HTMLElement>("#account-root");
if (!root) throw new Error("Account root is missing");

const trimEnv = (value: string | undefined): string => value?.trim() ?? "";
const { supabaseUrl, supabaseKey } = resolveAccountAuthConfig(import.meta.env);
const isAuthConfigured = Boolean(supabaseUrl && supabaseKey);
const turnstileSiteKey = trimEnv(import.meta.env.VITE_TURNSTILE_SITE_KEY);
const supabase: SupabaseClient | null = isAuthConfigured
  ? createClient(supabaseUrl, supabaseKey, {
      auth: {
        flowType: "pkce",
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
        storage: window.sessionStorage,
        storageKey: "sehaj-account-auth-v1"
      }
    })
  : null;

const previewMode = accountPreviewMode(window.location.search, import.meta.env.DEV);
const RETURN_CONTEXT_KEY = "sehaj:account-return:v1";
const RETURN_CONTEXT_MAX_AGE_MS = 15 * 60 * 1000;
const CHECKOUT_ATTEMPT_KEY = "sehaj:account-checkout:v2";
const CHECKOUT_ATTEMPT_MAX_AGE_MS = 30 * 60 * 1000;
const supportUrl = "/support/";
const accountUrl = `${window.location.origin}/account/`;

type ReturnContext = {
  state: string | null;
  checkout: "success" | "cancelled" | null;
  createdAt: number;
};

let activeSession: Session | null = null;
let activeStatus: AccountStatus | null = null;
let selectedPlanId: AccountPlanId = "annual";
let otpEmail = "";
let otpSent = false;
let authMessage = "";
let actionMessage = "";
let isBusy = false;
let turnstileToken = "";
let turnstileWidgetId: string | null = null;
let turnstileGeneration = 0;
let turnstileLoadPromise: Promise<void> | null = null;

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      theme: "light";
      size: "flexible";
      callback: (token: string) => void;
      "error-callback": () => void;
      "expired-callback": () => void;
    }
  ) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type CheckoutAttempt = {
  idempotencyKey: string;
  planId: AccountPlanId;
  createdAt: number;
};

const checkoutIdempotencyKey = (planId: AccountPlanId): string => {
  const raw = sessionStorage.getItem(CHECKOUT_ATTEMPT_KEY);
  if (raw) {
    try {
      const attempt = JSON.parse(raw) as Partial<CheckoutAttempt>;
      if (
        attempt.planId === planId &&
        typeof attempt.idempotencyKey === "string" &&
        typeof attempt.createdAt === "number" &&
        Date.now() - attempt.createdAt <= CHECKOUT_ATTEMPT_MAX_AGE_MS
      ) {
        return attempt.idempotencyKey;
      }
    } catch {
      // Replace malformed browser state with a fresh attempt below.
    }
  }

  const attempt: CheckoutAttempt = {
    idempotencyKey: crypto.randomUUID(),
    planId,
    createdAt: Date.now()
  };
  sessionStorage.setItem(CHECKOUT_ATTEMPT_KEY, JSON.stringify(attempt));
  return attempt.idempotencyKey;
};

const renderIcons = (): void => {
  createIcons({
    icons: {
      AlertCircle,
      ArrowRight,
      BadgeCheck,
      BookOpen,
      CalendarDays,
      ChevronRight,
      Headphones,
      Languages,
      LifeBuoy,
      LoaderCircle,
      LockKeyhole,
      LogOut,
      Mail,
      Menu,
      RefreshCw,
      ShieldCheck,
      UserRound,
      X
    },
    attrs: {
      "aria-hidden": "true",
      "stroke-width": 1.8
    }
  });
};

const persistReturnContext = (): ReturnContext | null => {
  const params = new URLSearchParams(window.location.search);
  const checkout = params.get("checkout");
  const state = params.get("state")?.trim() ?? "";

  if (checkout === "success" || checkout === "cancelled") {
    window.history.replaceState({}, document.title, "/account/");
    if (!state || state.length > 256) {
      sessionStorage.removeItem(RETURN_CONTEXT_KEY);
      return null;
    }
    const context: ReturnContext = {
      state,
      checkout,
      createdAt: Date.now()
    };
    sessionStorage.setItem(RETURN_CONTEXT_KEY, JSON.stringify(context));
    return context;
  }

  const raw = sessionStorage.getItem(RETURN_CONTEXT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ReturnContext>;
    if (
      typeof parsed.createdAt !== "number" ||
      Date.now() - parsed.createdAt > RETURN_CONTEXT_MAX_AGE_MS ||
      (parsed.checkout !== "success" && parsed.checkout !== "cancelled") ||
      typeof parsed.state !== "string" ||
      !parsed.state ||
      parsed.state.length > 256
    ) {
      sessionStorage.removeItem(RETURN_CONTEXT_KEY);
      return null;
    }
    return {
      state: parsed.state,
      checkout: parsed.checkout,
      createdAt: parsed.createdAt
    };
  } catch {
    sessionStorage.removeItem(RETURN_CONTEXT_KEY);
    return null;
  }
};

let returnContext = persistReturnContext();

const clearReturnContext = (): void => {
  sessionStorage.removeItem(RETURN_CONTEXT_KEY);
  returnContext = null;
};

const brand = (centered = false): string => `
  <a class="account-brand${centered ? " account-brand-centered" : ""}" href="/" aria-label="Sehaj home">
    <img src="/sehaj/brand/sehaj-icon.png" alt="" width="40" height="40" />
    <span>Sehaj</span>
  </a>`;

const legalLinks = (): string => `
  <p class="account-legal-copy">
    By continuing, you agree to our
    <a href="/terms/">Terms and Conditions</a>
    and
    <a href="/privacy/">Privacy Policy</a>.
  </p>`;

const loadTurnstile = (): Promise<void> => {
  if (!turnstileSiteKey || window.turnstile) return Promise.resolve();
  if (turnstileLoadPromise) return turnstileLoadPromise;

  turnstileLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      turnstileLoadPromise = null;
      script.remove();
      reject(new Error("Security check could not load."));
    };
    document.head.append(script);
  });
  return turnstileLoadPromise;
};

const cleanupTurnstile = (): void => {
  if (turnstileWidgetId && window.turnstile) {
    try {
      window.turnstile.remove(turnstileWidgetId);
    } catch {
      // The previous render may already have removed its container.
    }
  }
  turnstileWidgetId = null;
  turnstileToken = "";
};

const setTurnstileState = (token: string, message = ""): void => {
  turnstileToken = token;
  const submit = root.querySelector<HTMLButtonElement>("#account-email-submit");
  if (submit) submit.disabled = isBusy || (Boolean(turnstileSiteKey) && !token);
  const status = root.querySelector<HTMLElement>("#account-turnstile-status");
  if (status) status.textContent = message;
};

const mountTurnstile = async (generation: number): Promise<void> => {
  if (!turnstileSiteKey) return;
  const container = root.querySelector<HTMLElement>("#account-turnstile");
  if (!container) return;

  try {
    await loadTurnstile();
    if (generation !== turnstileGeneration || !container.isConnected) return;
    if (!window.turnstile) throw new Error("Security check could not start.");
    turnstileWidgetId = window.turnstile.render(container, {
      sitekey: turnstileSiteKey,
      theme: "light",
      size: "flexible",
      callback: (token) => setTurnstileState(token),
      "error-callback": () => setTurnstileState("", "Security check unavailable. Please try again."),
      "expired-callback": () => setTurnstileState("", "Please complete the security check again.")
    });
  } catch (error) {
    setTurnstileState("", error instanceof Error ? error.message : "Security check could not load.");
  }
};

const accountNavigation = (current: "account" | "plan" | "support"): string => `
  <nav class="account-nav" aria-label="Account navigation">
    <a href="/account/"${current === "account" ? ' aria-current="page"' : ""}>
      <i data-lucide="user-round"></i><span>Account</span>
    </a>
    <a href="${current === "plan" ? "#plans" : "/account/#plans"}"${current === "plan" ? ' aria-current="page"' : ""}>
      <i data-lucide="calendar-days"></i><span>Plan</span>
    </a>
    <a href="${supportUrl}"${current === "support" ? ' aria-current="page"' : ""}>
      <i data-lucide="life-buoy"></i><span>Support</span>
    </a>
  </nav>`;

const signedInFrame = (
  content: string,
  current: "account" | "plan" | "support" = "account"
): string => `
  <header class="account-mobile-header">
    ${brand()}
    <button class="account-icon-button" type="button" data-action="open-menu" aria-label="Open account menu">
      <i data-lucide="menu"></i>
    </button>
  </header>
  <dialog class="account-menu" id="account-menu" aria-label="Account menu">
    <div class="account-menu-header">
      ${brand()}
      <button class="account-icon-button" type="button" data-action="close-menu" aria-label="Close account menu">
        <i data-lucide="x"></i>
      </button>
    </div>
    ${accountNavigation(current)}
    <button class="account-nav-signout" type="button" data-action="sign-out">
      <i data-lucide="log-out"></i><span>Sign out</span>
    </button>
  </dialog>
  <div class="account-layout">
    <aside class="account-sidebar">
      ${brand()}
      ${accountNavigation(current)}
      <button class="account-nav-signout" type="button" data-action="sign-out">
        <i data-lucide="log-out"></i><span>Sign out</span>
      </button>
    </aside>
    <main class="account-main" id="account-content">
      ${content}
    </main>
  </div>`;

const bindMenu = (): void => {
  const dialog = root.querySelector<HTMLDialogElement>("#account-menu");
  root.querySelector<HTMLElement>("[data-action='open-menu']")?.addEventListener("click", () => {
    dialog?.showModal();
  });
  root.querySelector<HTMLElement>("[data-action='close-menu']")?.addEventListener("click", () => {
    dialog?.close();
  });
  dialog?.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
};

const bindSignOut = (): void => {
  for (const button of root.querySelectorAll<HTMLButtonElement>("[data-action='sign-out']")) {
    button.addEventListener("click", () => void signOut());
  }
};

const finishRender = (): void => {
  renderIcons();
  bindMenu();
  bindSignOut();
};

const renderTransition = (title: string, detail: string): void => {
  cleanupTurnstile();
  root.innerHTML = `
    <main class="account-transition" id="account-content" aria-busy="true">
      <img class="account-transition-backdrop" src="/sehaj/account/sign-in-reading-room.webp" alt="" />
      <section class="account-transition-card" role="status" aria-live="polite">
        <span class="account-transition-mark" aria-hidden="true">
          <img src="/sehaj/brand/sehaj-icon.png" alt="" width="72" height="72" />
          <i class="account-transition-spinner" data-lucide="loader-circle"></i>
        </span>
        <p class="account-kicker">Sehaj</p>
        <h1>${escapeAccountHtml(title)}</h1>
        <p>${escapeAccountHtml(detail)}</p>
      </section>
    </main>`;
  renderIcons();
};

const renderSignIn = (message = authMessage): void => {
  cleanupTurnstile();
  const currentTurnstileGeneration = ++turnstileGeneration;
  root.innerHTML = `
    <header class="account-auth-header">${brand()}</header>
    <main class="account-auth-main" id="account-content">
      <section class="account-auth-visual" aria-label="A quiet Sehaj practice">
        <img src="/sehaj/account/sign-in-reading-room.webp" alt="" />
        <div class="account-auth-visual-copy">
          <p class="account-kicker">A quiet daily practice</p>
          <h2>Return to the stories that steady you.</h2>
          <p>Read, listen, and reflect in English or Punjabi, wherever the day finds you.</p>
          <ul aria-label="Sehaj membership features">
            <li><i data-lucide="book-open"></i> Read</li>
            <li><i data-lucide="headphones"></i> Listen</li>
            <li><i data-lucide="languages"></i> English</li>
            <li><i data-lucide="languages"></i> Punjabi</li>
          </ul>
        </div>
      </section>
      <img class="account-auth-decoration" src="/sehaj/Elements/plant-elment-dark-beige-right-transparent.png" alt="" aria-hidden="true" />
      <section class="account-auth-panel" aria-labelledby="account-sign-in-title">
        <span class="account-auth-panel-mark" aria-hidden="true">
          <img src="/sehaj/brand/sehaj-icon.png" alt="" width="68" height="68" />
        </span>
        <p class="account-kicker">Your account</p>
        <h1 id="account-sign-in-title">Sign in to Sehaj</h1>
        <p class="account-auth-intro">Use the same account as the Sehaj app to keep your stories, reflections, and listening progress together.</p>

        ${message ? `<div class="account-inline-message" role="status">${escapeAccountHtml(message)}</div>` : ""}

        <div class="account-auth-actions">
          <button class="account-button account-button-provider" type="button" data-provider="google"${isBusy ? " disabled" : ""}>
            Continue with Google
          </button>
        </div>

        <div class="account-divider"><span>or</span></div>

        <form class="account-email-form" id="account-email-form">
          <label for="account-email">Email address</label>
          <div class="account-input-wrap">
            <i data-lucide="mail"></i>
            <input
              id="account-email"
              name="email"
              type="email"
              inputmode="email"
              autocomplete="email"
              placeholder="you@example.com"
              value="${escapeAccountHtml(otpEmail)}"
              required
            />
          </div>
          ${turnstileSiteKey ? `
            <div class="account-turnstile" id="account-turnstile"></div>
            <p class="account-turnstile-status" id="account-turnstile-status" role="status"></p>` : ""}
          <button class="account-button account-button-secondary" id="account-email-submit" type="submit"${isBusy || Boolean(turnstileSiteKey) ? " disabled" : ""}>
            ${isBusy ? '<i class="account-spin" data-lucide="loader-circle"></i> Sending code' : "Email me a code"}
          </button>
        </form>

        ${otpSent ? `
          <form class="account-code-form" id="account-code-form">
            <label for="account-code">Six-digit code</label>
            <input id="account-code" name="code" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{8}" maxlength="8" required />
            <button class="account-button account-button-primary" type="submit"${isBusy ? " disabled" : ""}>Confirm and continue</button>
          </form>` : ""}

        ${legalLinks()}
        <p class="account-auth-security"><i data-lucide="lock-keyhole"></i> Secure sign-in powered by your Sehaj account.</p>
      </section>
    </main>`;

  for (const button of root.querySelectorAll<HTMLButtonElement>("[data-provider]")) {
    button.addEventListener("click", () => void beginOAuth(button.dataset.provider));
  }
  root.querySelector<HTMLFormElement>("#account-email-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void sendOtp(new FormData(event.currentTarget as HTMLFormElement));
  });
  root.querySelector<HTMLFormElement>("#account-code-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void verifyOtp(new FormData(event.currentTarget as HTMLFormElement));
  });
  renderIcons();
  void mountTurnstile(currentTurnstileGeneration);
};

const accountHeading = (status: AccountStatus): string => `
  <div class="account-page-heading">
    <div>
      <p class="account-kicker">Your account</p>
      <h1>${status.access === "active" ? "Welcome back" : "Continue your Sehaj journey"}</h1>
      <p class="account-email">Signed in as ${escapeAccountHtml(status.email)}</p>
    </div>
  </div>`;

const storeLinks = (): string => `
  <div class="account-store-links" aria-label="Download Sehaj">
    <a href="https://apps.apple.com/au/app/sehaj-daily-sikh-stories/id6787699607" aria-label="Download Sehaj on the App Store">
      <img src="/download-on-the-app-store.svg" alt="Download Sehaj on the App Store" />
    </a>
    <a href="https://play.google.com/store/apps/details?id=app.sehaj.mobile" aria-label="Get Sehaj on Google Play">
      <img src="/google_play.svg" alt="Get Sehaj on Google Play" />
    </a>
  </div>`;

const formatMembershipDate = (value: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(date);
};

const renderActive = (status: AccountStatus): void => {
  activeStatus = status;
  const nativeManagement = nativeMembershipManagement(status.membership.nativeBillingStore);
  const trialEnd = status.membership.kind === "trial"
    ? formatMembershipDate(status.membership.renewsOrEndsAtUtc)
    : null;
  const accessLabel = status.membership.kind === "trial"
    ? "Trial active"
    : status.membership.kind === "complimentary"
      ? "Complimentary access"
      : "Access active";
  const activeDescription = nativeManagement
    ? nativeManagement.description
    : status.membership.kind === "trial"
      ? trialEnd
        ? `Your free trial runs until ${escapeAccountHtml(trialEnd)}. Manage it any time from this account.`
        : "Your free trial is active. Manage it any time from this account."
      : "Continue reading, listening, and carrying your reflections with you.";
  root.innerHTML = signedInFrame(`
    <section class="account-state account-state-active">
      ${accountHeading(status)}
      ${actionMessage ? `<div class="account-inline-message" role="status">${escapeAccountHtml(actionMessage)}</div>` : ""}
      <div class="account-member-hero">
        <img src="/sehaj/account/active-rhythm.webp" alt="" />
        <div class="account-member-hero-copy">
          <span class="account-status-badge"><i data-lucide="badge-check"></i>${accessLabel}</span>
          <p class="account-kicker">Your Sehaj practice</p>
          <h2>Your next Saakhi is ready.</h2>
          <p>${activeDescription}</p>
        </div>
      </div>
      <div class="account-active-actions">
        <a class="account-button account-button-primary account-open-button" href="/open/account/">
          Open Sehaj <i data-lucide="arrow-right"></i>
        </a>
        <div class="account-action-list">
          ${nativeManagement?.url ? `
            <a href="${nativeManagement.url}" target="_blank" rel="noopener noreferrer">
              <span><i data-lucide="calendar-days"></i>${nativeManagement.label}</span>
              <i data-lucide="arrow-right"></i>
            </a>` : ""}
          ${status.canManagePlan ? `
            <button type="button" data-action="manage-plan"${isBusy ? " disabled" : ""}>
              <span><i data-lucide="calendar-days"></i>Manage plan</span>
              ${isBusy ? '<i class="account-spin" data-lucide="loader-circle"></i>' : '<i data-lucide="chevron-right"></i>'}
            </button>` : ""}
          <a href="${supportUrl}">
            <span><i data-lucide="life-buoy"></i>Contact support</span>
            <i data-lucide="chevron-right"></i>
          </a>
        </div>
      </div>
      ${storeLinks()}
      <div class="account-quiet-links">
        <a href="/terms/">Terms and Conditions</a>
        <a href="/privacy/">Privacy Policy</a>
      </div>
    </section>`);

  root.querySelector<HTMLButtonElement>("[data-action='manage-plan']")?.addEventListener("click", () => {
    void openPlanManagement();
  });
  finishRender();
};

const renderNoAccess = (status: AccountStatus, notice = actionMessage): void => {
  activeStatus = status;
  const planPriority: Record<AccountPlanId, number> = { annual: 0, quarterly: 1, monthly: 2 };
  const displayPlans = [...status.plans].sort((left, right) => planPriority[left.id] - planPriority[right.id]);
  const primaryPlan = displayPlans.find((plan) => plan.id === "annual")
    ?? displayPlans.find((plan) => plan.recommended)
    ?? displayPlans[0]
    ?? null;
  if (!status.plans.some((plan) => plan.id === selectedPlanId)) {
    selectedPlanId = primaryPlan?.id ?? "annual";
  }

  const primaryPrice = primaryPlan ? formatAccountPlanPrice(primaryPlan) : null;
  const primaryReferenceAmount = primaryPlan
    ? accountPlanReferenceAmount(primaryPlan, status.plans)
    : null;
  const primarySavings = primaryPlan
    ? accountPlanSavingsPercent(primaryPlan, status.plans)
    : null;
  const secondaryPlans = displayPlans.filter((plan) => plan.id !== primaryPlan?.id);
  const secondaryPlanMarkup = secondaryPlans.map((plan) => {
    const price = formatAccountPlanPrice(plan);
    const savings = accountPlanSavingsPercent(plan, status.plans);
    const isOpening = isBusy && selectedPlanId === plan.id;
    return `
      <article class="account-secondary-plan">
        <div class="account-secondary-plan-copy">
          <div>
            <h3>${escapeAccountHtml(plan.label)}</h3>
            ${savings ? `<span class="account-secondary-saving">Save ${savings}%</span>` : ""}
          </div>
          <p>${escapeAccountHtml(price.cadence)}${price.equivalent ? ` · ${escapeAccountHtml(price.equivalent)}` : ""}</p>
        </div>
        <button
          class="account-button account-button-secondary account-secondary-checkout"
          type="button"
          data-action="checkout"
          data-plan-id="${plan.id}"
          ${isBusy || !status.canChoosePlan ? "disabled" : ""}
        >
          ${isOpening ? '<i class="account-spin" data-lucide="loader-circle"></i> Opening checkout' : `Choose ${escapeAccountHtml(plan.label)}`}
        </button>
      </article>`;
  }).join("");
  const isPrimaryOpening = Boolean(primaryPlan && isBusy && selectedPlanId === primaryPlan.id);
  const otherPlansOpen = Boolean(primaryPlan && selectedPlanId !== primaryPlan.id);
  const primaryHasTrial = Boolean(primaryPlan?.trialDays);

  root.innerHTML = signedInFrame(`
    <section class="account-state account-state-plan" id="plans">
      ${accountHeading(status)}
      ${notice ? `<div class="account-inline-message" role="status">${escapeAccountHtml(notice)}</div>` : ""}
      <div class="account-plan-hero">
        <img src="/sehaj/account/plan-storytelling.webp" alt="" />
        <div class="account-plan-hero-copy">
          <p class="account-kicker">A little Sikhi, every day</p>
          <h2>Stories to carry. Wisdom to live.</h2>
          <p>Build a quieter daily connection through carefully told Saakhis.</p>
          <ul class="account-plan-feature-list" aria-label="Included with Sehaj">
            <li><i data-lucide="book-open"></i>Read</li>
            <li><i data-lucide="headphones"></i>Listen</li>
            <li><i data-lucide="languages"></i>English</li>
            <li><i data-lucide="languages"></i>Punjabi</li>
          </ul>
        </div>
      </div>
      ${primaryPlan && primaryPrice ? `
        <section class="account-primary-offer" aria-labelledby="account-primary-offer-title">
          <div class="account-primary-offer-heading">
            <div>
              <p class="account-kicker">${primaryHasTrial ? `${primaryPlan?.trialDays} days free` : "Best value"}</p>
              <h3 id="account-primary-offer-title">${escapeAccountHtml(primaryPlan.label)}</h3>
              <p>${primaryHasTrial ? "Full access today. Annual billing starts after your trial." : "One year of complete Sehaj access."}</p>
            </div>
            ${primarySavings ? `<span class="account-primary-saving">Save ${primarySavings}%</span>` : ""}
          </div>
          <div class="account-primary-price">
            ${primaryHasTrial ? `
              <span class="account-primary-reference">
                <small>Due today</small>
                <strong>Free</strong>
              </span>` : primaryReferenceAmount ? `
              <span class="account-primary-reference">
                <small>12 months at the monthly rate</small>
                <del>${escapeAccountHtml(formatAccountCurrency(primaryReferenceAmount, primaryPlan.currency))}</del>
              </span>` : ""}
            <span class="account-primary-current">
              <strong>${escapeAccountHtml(primaryPrice.total)}</strong>
              ${primaryHasTrial
                ? `<small>after ${primaryPlan.trialDays} days, then yearly</small>`
                : primaryPrice.equivalent ? `<small>${escapeAccountHtml(primaryPrice.equivalent)}</small>` : ""}
            </span>
          </div>
          <button
            class="account-button account-button-primary account-checkout-button"
            type="button"
            data-action="checkout"
            data-plan-id="${primaryPlan.id}"
            ${isBusy || !status.canChoosePlan ? "disabled" : ""}
          >
            ${isPrimaryOpening ? '<i class="account-spin" data-lucide="loader-circle"></i> Opening secure checkout' : primaryHasTrial ? `Start my ${primaryPlan.trialDays}-day free trial <i data-lucide="arrow-right"></i>` : `Continue with ${escapeAccountHtml(primaryPlan.label)} <i data-lucide="arrow-right"></i>`}
          </button>
          <p class="account-billing-disclosure">${primaryHasTrial
            ? `Free for ${primaryPlan.trialDays} days, then ${escapeAccountHtml(primaryPrice.cadence)} unless cancelled. Cancel anytime from your Sehaj account. Taxes may apply.`
            : `Charged ${escapeAccountHtml(primaryPrice.cadence)}. Renews automatically until cancelled. Cancel anytime from your Sehaj account. Taxes may apply.`}</p>
          <div class="account-offer-trust" aria-label="Checkout assurances">
            <span><i data-lucide="lock-keyhole"></i>Secure checkout</span>
            <span><i data-lucide="shield-check"></i>Cancel anytime</span>
          </div>
          <p class="account-checkout-legal">
            By continuing, you agree to our <a href="/terms/">Terms and Conditions</a> and <a href="/privacy/">Privacy Policy</a>.
            Refund eligibility follows the terms shown at checkout and applicable law.
          </p>
        </section>` : `
        <div class="account-inline-message account-plan-unavailable" role="status">Plan details are unavailable right now. Please refresh or contact support.</div>`}
      ${secondaryPlanMarkup ? `
        <details class="account-other-plans"${otherPlansOpen ? " open" : ""}>
          <summary>
            <span><strong>Other plans</strong><small>Quarterly and monthly options</small></span>
            <i data-lucide="chevron-right"></i>
          </summary>
          <div class="account-secondary-plans">${secondaryPlanMarkup}</div>
        </details>` : ""}
      <section class="account-product-proof" aria-labelledby="account-product-proof-title">
        <div>
          <p class="account-kicker">Inside Sehaj</p>
          <h3 id="account-product-proof-title">A daily practice designed to feel light.</h3>
        </div>
        <div class="account-proof-rail">
          <img src="/sehaj/app-screenshots/01-home.webp" alt="Sehaj daily Saakhi screen" />
          <img src="/sehaj/app-screenshots/02-saakhi.webp" alt="Sehaj story library" />
          <img src="/sehaj/app-screenshots/03-listen.webp" alt="Sehaj reading and listening screen" />
        </div>
      </section>
      <a class="account-text-link account-open-secondary" href="/open/account/">Already use Sehaj? Open the app</a>
      <div class="account-quiet-links">
        <a href="/terms/">Terms and Conditions</a>
        <a href="/privacy/">Privacy Policy</a>
        <a href="${supportUrl}">Contact support</a>
      </div>
    </section>`, "plan");

  for (const button of root.querySelectorAll<HTMLButtonElement>("[data-action='checkout']")) {
    button.addEventListener("click", () => {
      const planId = button.dataset.planId;
      if (!isAccountPlanId(planId)) return;
      selectedPlanId = planId;
      void openCheckout();
    });
  }
  finishRender();
};

const renderChecking = (status?: AccountStatus): void => {
  const checkingStatus = status ?? previewStatus("checking");
  root.innerHTML = signedInFrame(`
    <section class="account-state account-state-centered">
      <span class="account-state-icon"><i class="account-spin" data-lucide="loader-circle"></i></span>
      <p class="account-kicker">Your account</p>
      <h1>Checking access</h1>
      <p>We are confirming your Sehaj access. This usually takes only a moment.</p>
      <div class="account-state-actions">
        <button class="account-button account-button-secondary" type="button" data-action="refresh">
          <i data-lucide="refresh-cw"></i>Refresh
        </button>
        <a class="account-text-link" href="${supportUrl}">Contact support</a>
      </div>
    </section>`);
  root.querySelector<HTMLButtonElement>("[data-action='refresh']")?.addEventListener("click", () => {
    void loadAccountStatus();
  });
  finishRender();
  activeStatus = checkingStatus;
};

const renderError = (message = "We could not load your account right now."): void => {
  const email = activeSession?.user.email ?? "you@example.com";
  const safeStatus: AccountStatus = {
    access: "unknown",
    email,
    canManagePlan: false,
    canChoosePlan: false,
    membership: { kind: "inactive", planId: null, renewsOrEndsAtUtc: null, nativeBillingStore: null },
    plans: []
  };
  root.innerHTML = signedInFrame(`
    <section class="account-state account-state-centered">
      <span class="account-state-icon account-state-icon-error"><i data-lucide="alert-circle"></i></span>
      <p class="account-kicker">Your account</p>
      <h1>Could not load account</h1>
      <p>${escapeAccountHtml(message)}</p>
      <div class="account-state-actions">
        <button class="account-button account-button-secondary" type="button" data-action="refresh">
          <i data-lucide="refresh-cw"></i>Try again
        </button>
        <a class="account-text-link" href="${supportUrl}">Contact support</a>
      </div>
    </section>`);
  root.querySelector<HTMLButtonElement>("[data-action='refresh']")?.addEventListener("click", () => {
    void loadAccountStatus();
  });
  finishRender();
  activeStatus = safeStatus;
};

const requireSupabase = (): SupabaseClient => {
  if (!supabase) {
    throw new Error("Account sign-in is not configured for this build.");
  }
  return supabase;
};

const beginOAuth = async (provider: string | undefined): Promise<void> => {
  if (provider !== "google") return;
  isBusy = true;
  authMessage = "";
  renderTransition("Opening secure sign-in", "Connecting to Google. This can take a moment.");
  try {
    const { error } = await requireSupabase().auth.signInWithOAuth({
      provider,
      options: { redirectTo: accountUrl }
    });
    if (error) throw error;
  } catch (error) {
    isBusy = false;
    authMessage = error instanceof Error ? error.message : "Sign-in could not be started.";
    renderSignIn();
  }
};

const sendOtp = async (formData: FormData): Promise<void> => {
  const email = String(formData.get("email") ?? "").trim().toLowerCase();
  if (!/^\S+@\S+\.\S+$/.test(email)) {
    authMessage = "Enter a valid email address.";
    renderSignIn();
    return;
  }
  if (turnstileSiteKey && !turnstileToken) {
    authMessage = "Complete the security check before requesting a code.";
    renderSignIn();
    return;
  }

  otpEmail = email;
  const captchaToken = turnstileToken;
  isBusy = true;
  authMessage = "";
  renderTransition("Sending your sign-in code", `We are preparing a secure code for ${email}.`);
  try {
    const { error } = await requireSupabase().auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: accountUrl,
        shouldCreateUser: true,
        ...(captchaToken ? { captchaToken } : {})
      }
    });
    if (error) throw error;
    otpSent = true;
    authMessage = "We sent a sign-in code to your email.";
  } catch (error) {
    authMessage = error instanceof Error ? error.message : "The code could not be sent.";
  } finally {
    isBusy = false;
    renderSignIn();
  }
};

const verifyOtp = async (formData: FormData): Promise<void> => {
  const token = String(formData.get("code") ?? "").trim();
  if (!/^\d{8}$/.test(token)) {
    authMessage = "Enter the eight-digit code from your email.";
    renderSignIn();
    return;
  }

  isBusy = true;
  renderTransition("Confirming your code", "One quiet moment while we open your Sehaj account.");
  try {
    const { data, error } = await requireSupabase().auth.verifyOtp({
      email: otpEmail,
      token,
      type: "email"
    });
    if (error) throw error;
    activeSession = data.session;
    otpSent = false;
    authMessage = "";
    await loadAccountStatus();
  } catch (error) {
    authMessage = error instanceof Error ? error.message : "That code could not be confirmed.";
    isBusy = false;
    renderSignIn();
  }
};

const signOut = async (): Promise<void> => {
  isBusy = true;
  renderTransition("Signing you out", "Closing your Sehaj session securely.");
  try {
    await supabase?.auth.signOut();
  } finally {
    activeSession = null;
    activeStatus = null;
    clearReturnContext();
    isBusy = false;
    renderSignIn();
  }
};

const currentAccessToken = async (): Promise<string> => {
  const client = requireSupabase();
  const { data, error } = await client.auth.getSession();
  if (error || !data.session?.access_token) {
    throw new Error("Your session has expired. Please sign in again.");
  }
  activeSession = data.session;
  return data.session.access_token;
};

const displayStatus = (status: AccountStatus): void => {
  if (status.access === "active") {
    sessionStorage.removeItem(CHECKOUT_ATTEMPT_KEY);
    clearReturnContext();
    renderActive(status);
  } else if (status.access === "inactive") {
    renderNoAccess(status);
  } else if (status.access === "checking") {
    renderChecking(status);
  } else {
    renderError("We could not confirm access. Please try again or contact support.");
  }
};

const sleep = (milliseconds: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const loadAccountStatus = async (): Promise<void> => {
  if (previewMode === "active" || previewMode === "no-access" || previewMode === "checking") {
    displayStatus(previewStatus(previewMode));
    return;
  }
  if (previewMode === "error") {
    renderError();
    return;
  }

  renderChecking();
  try {
    const token = await currentAccessToken();
    const delays = returnContext?.checkout === "success" ? [0, 1200, 2400, 4200, 7000] : [0];
    let status: AccountStatus | null = null;

    for (const delay of delays) {
      if (delay > 0) await sleep(delay);
      try {
        status = await fetchAccountStatus(token, returnContext?.state ?? null);
      } catch (error) {
        if (!returnContext?.state) throw error;
        clearReturnContext();
        status = await fetchAccountStatus(token);
      }
      if (status.access === "active" || status.access === "unknown") break;
    }

    if (!status) throw new Error("The account response was incomplete.");
    if (returnContext?.checkout === "success" && status.access !== "active") {
      actionMessage = "Your checkout finished. Access may take another moment to appear.";
    }
    if (returnContext?.checkout === "cancelled") {
      actionMessage = "No changes were made to your account.";
      clearReturnContext();
    }
    displayStatus(status);
  } catch (error) {
    const message = error instanceof Error ? error.message : "We could not load your account right now.";
    if (message.toLowerCase().includes("session")) {
      activeSession = null;
      authMessage = message;
      renderSignIn();
      return;
    }
    renderError(message);
  } finally {
    isBusy = false;
  }
};

const openCheckout = async (): Promise<void> => {
  if (isBusy || !activeStatus?.canChoosePlan) return;
  isBusy = true;
  actionMessage = "";
  renderNoAccess(activeStatus);
  try {
    const token = await currentAccessToken();
    const navigationUrl = await createAccountCheckout(
      token,
      selectedPlanId,
      checkoutIdempotencyKey(selectedPlanId)
    );
    window.location.assign(navigationUrl);
  } catch (error) {
    isBusy = false;
    actionMessage = error instanceof Error ? error.message : "Secure checkout could not be opened.";
    renderNoAccess(activeStatus);
  }
};

const openPlanManagement = async (): Promise<void> => {
  if (isBusy || !activeStatus?.canManagePlan) return;
  isBusy = true;
  actionMessage = "";
  renderActive(activeStatus);
  try {
    const token = await currentAccessToken();
    const navigationUrl = await createAccountPortalSession(token, crypto.randomUUID());
    window.location.assign(navigationUrl);
  } catch (error) {
    isBusy = false;
    actionMessage = error instanceof Error ? error.message : "Plan management could not be opened.";
    renderActive(activeStatus);
  }
};

const completeAuthCallback = async (): Promise<void> => {
  if (!supabase) return;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  if (!code) return;

  const { error } = await supabase.auth.exchangeCodeForSession(code);
  window.history.replaceState({}, document.title, "/account/");
  if (error) throw error;
};

const bootstrap = async (): Promise<void> => {
  if (previewMode === "sign-in") {
    renderSignIn();
    return;
  }
  if (previewMode === "loading") {
    renderTransition("Opening your account", "Bringing your stories and access together.");
    return;
  }
  if (previewMode === "active" || previewMode === "no-access" || previewMode === "checking" || previewMode === "error") {
    await loadAccountStatus();
    return;
  }
  if (!supabase) {
    authMessage = "Account sign-in is not configured for this local build.";
    renderSignIn();
    return;
  }

  try {
    await completeAuthCallback();
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    activeSession = data.session;
    if (!activeSession) {
      if (returnContext?.checkout === "success") {
        authMessage = "Sign in with the same Sehaj account to finish checking access.";
      } else if (returnContext?.checkout === "cancelled") {
        authMessage = "No changes were made. Sign in to return to your account.";
        clearReturnContext();
      }
      renderSignIn();
      return;
    }
    await loadAccountStatus();
  } catch (error) {
    activeSession = null;
    authMessage = error instanceof Error ? error.message : "Sign-in could not be completed.";
    renderSignIn();
  }
};

void bootstrap();
